import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Methodology() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    fetch(`${API}/public/methodology-config`).then(r => r.json())
      .then(setCfg).catch(() => setCfg({}));
  }, []);
  const ac = cfg && cfg.auto_confirm;
  return (
    <section data-testid="methodology-section" className="space-y-8">
      <h1 className="hs-display text-3xl md:text-4xl">Methodology</h1>
      <p className="text-muted max-w-3xl">
        HerdSafe reads text metadata from public registries and web pages — never installs,
        imports or executes third-party code. We score deterministically with a public
        rules pack, call an LLM only on an ambiguous or sampled subset, and publish only
        <strong className="text-fg"> confirmed</strong> items. Every public &quot;malicious&quot; answer says how it was
        confirmed: <code className="hs-mono">basis: human_review</code> or, only when{" "}
        <a href="#automated-confirmation" className="hs-link">automated confirmation</a> is switched on,{" "}
        <code className="hs-mono">basis: automated</code>.
      </p>

      <ol className="list-decimal space-y-4 pl-6 text-muted max-w-3xl">
        <li>
          <strong className="text-fg">Observe</strong> — crawlers fetch text only,
          obey <code className="hs-mono">robots.txt</code>, cap bytes per fetch, and
          per-host rate limit.
        </li>
        <li>
          <strong className="text-fg">Normalise</strong> — NFKC + invisible-char
          markers (<code className="hs-mono">⟦U+NNNN⟧</code>).
        </li>
        <li>
          <strong className="text-fg">Rules</strong> — deterministic detectors; each
          produces a weight; score is <code className="hs-mono">1 - exp(-Σw)</code>.
        </li>
        <li>
          <strong className="text-fg">LLM assist</strong> — called only above a score
          threshold or on a sampled benign subset.
        </li>
        <li>
          <strong className="text-fg">Tier</strong> — <code className="hs-mono">none → heuristic → suspected → auto_confirmed → confirmed</code>, plus
          <code className="hs-mono"> cleared</code>.
        </li>
        <li>
          <strong className="text-fg">Disclose</strong> — see the <Link to="/disclosure-policy" className="hs-link">disclosure policy</Link>.
        </li>
      </ol>

      <div id="automated-confirmation" data-testid="methodology-auto-confirm" className="space-y-3 text-muted max-w-3xl">
        <h2 className="text-fg">Automated confirmation</h2>
        <p data-testid="methodology-auto-confirm-mode">
          Status:{" "}
          <strong className="text-fg">
            {!ac && "loading…"}
            {ac && ac.mode === "on" && "on. Some malicious answers are confirmed automatically (basis: automated)."}
            {ac && ac.mode === "shadow" && "shadow. It is being measured against human reviews; nothing it decides is public."}
            {ac && ac.mode !== "on" && ac.mode !== "shadow" && "off. Every malicious answer comes from a human review."}
          </strong>
        </p>
        <p>
          A person reviews most findings. When automated confirmation is on, an item can also be confirmed without
          waiting for a reviewer, but only if <em>all</em> of these hold for its current text:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>the rules score it {ac ? `${ac.min_rules_score}` : "0.7"} or higher, with at least one threat-level signal;</li>
          <li>our primary model calls it malicious with confidence {ac ? `${ac.min_model_confidence}` : "0.85"} or higher;</li>
          <li>a second, independent model (a different model, normally from a different provider) also calls it malicious
            and quotes the text. We check every quote against the text; a quote that is not really there does not count;</li>
          <li>no reviewer has marked any version of it benign, and no appeal about it has been upheld.</li>
        </ul>
        <p>
          It then stays internal for {ac ? `${ac.grace_hours}` : "24"} hours so a reviewer can undo it, and at most{" "}
          {ac ? ac.daily_max : 20} items a day can be confirmed this way; the rest wait for a person. A later benign review
          or an upheld appeal removes an automated confirmation at once, and a human decision always outranks it.
        </p>
        <p>
          What this does not promise: two models can make the same mistake, so agreement makes an error less likely but does
          not rule it out. Before switching it on we run it in shadow mode and compare what it would have done with what
          reviewers later decide. Our labelled test set must show at least{" "}
          {ac ? `${Math.round(ac.backtest_min_precision * 100)}%` : "98%"} precision for this path. That set is small and
          uses a simulated second model, so we treat it as a minimum check, not proof. If you rely on HerdSafe for blocking
          decisions, you can treat <code className="hs-mono">basis: automated</code> differently from{" "}
          <code className="hs-mono">basis: human_review</code>.
        </p>
      </div>

      <div data-testid="methodology-config" className="hs-card text-sm text-fg max-w-3xl">
        <h2 className="text-fg">Live configuration</h2>
        {!cfg && <div className="text-muted text-xs">loading…</div>}
        {cfg && (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs hs-mono">
            <dt>detector_version</dt>
            <dd data-testid="cfg-detector-version">{cfg.detector_version || "—"}</dd>
            <dt>rules_score_threshold</dt>
            <dd data-testid="cfg-threshold">{cfg.rules_score_threshold ?? "—"}</dd>
            <dt>random_sample_rate</dt>
            <dd>{cfg.random_sample_rate ?? "—"}</dd>
            <dt>backtest_min_recall</dt>
            <dd>{cfg.backtest_min_recall ?? "—"}</dd>
            <dt>backtest_min_precision</dt>
            <dd>{cfg.backtest_min_precision ?? "—"}</dd>
            <dt>techniques</dt>
            <dd>{(cfg.techniques || []).join(", ") || "—"}</dd>
            <dt>rules</dt>
            <dd>{(cfg.rules || []).map(r => `${r.id}(${r.weight})`).join(", ") || "—"}</dd>
          </dl>
        )}
      </div>
    </section>
  );
}
